<script setup>
// Inspector do nó "Capturar lead": liga/desliga + escolhe EM QUAL funil e etapa
// (CRM interno/kanban) o agente registra o lead ao capturá-lo. Sem funil, a
// ferramenta só atualiza o cadastro do contato (sem criar card no kanban).
// Guarda em data.funnelId + data.stageName.
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import FunnelsAPI from 'dashboard/api/funnels';

const props = defineProps({ data: { type: Object, required: true } });
const emit = defineEmits(['update', 'remove', 'close']);
const { t } = useI18n();

const funnels = ref([]);
const loading = ref(true);

const desc = computed(() => t('AI_AGENTS.FLOW.TOOLS.tool_capture_lead.DESC'));

const selectedFunnel = computed(() =>
  funnels.value.find(f => f.id === Number(props.data.funnelId))
);

// stages é um objeto { stage_name: { position, label, ... } } — ordena por
// position pra o select aparecer na ordem das colunas do kanban.
const stages = computed(() => {
  const raw = selectedFunnel.value?.stages || {};
  return Object.entries(raw)
    .map(([name, cfg]) => ({
      name,
      label: cfg?.label || name,
      position: cfg?.position ?? 999,
    }))
    .sort((a, b) => a.position - b.position);
});

const onFunnelChange = e => {
  const id = Number(e.target.value) || null;
  // Trocar de funil zera a etapa (as etapas são de cada funil).
  emit('update', { funnelId: id, stageName: null });
};

onMounted(async () => {
  try {
    const { data } = await FunnelsAPI.get();
    funnels.value = data || [];
  } catch {
    funnels.value = [];
  } finally {
    loading.value = false;
  }
});
</script>

<template>
  <div class="p-4">
    <div class="mb-3 flex items-center justify-between">
      <div class="flex items-center gap-2.5">
        <span
          class="flex size-8 shrink-0 items-center justify-center rounded-xl bg-n-teal-3 text-n-teal-11"
        >
          <span class="i-lucide-user-plus text-base" />
        </span>
        <h3 class="text-sm font-semibold text-n-slate-12">
          {{ data.label || t('AI_AGENTS.FLOW.LEAD.TITLE') }}
        </h3>
      </div>
      <button class="i-lucide-x text-n-slate-10" @click="emit('close')" />
    </div>

    <div class="mb-3 rounded-lg border border-n-weak bg-n-alpha-1 p-3">
      <p class="text-sm text-n-slate-12">{{ desc }}</p>
    </div>

    <!-- Liga/desliga -->
    <label
      class="mb-3 flex cursor-pointer items-center justify-between rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2.5"
    >
      <span class="text-sm font-medium text-n-slate-12">
        {{ t('AI_AGENTS.FLOW.TOOLS.ENABLED') }}
      </span>
      <input
        type="checkbox"
        class="size-4 shrink-0 accent-[#00897b]"
        :checked="data.enabled !== false"
        @change="emit('update', { enabled: $event.target.checked })"
      />
    </label>

    <!-- Funil de destino (CRM interno / kanban) -->
    <label class="mb-1 block text-xs font-medium text-n-slate-11">
      {{ t('AI_AGENTS.FLOW.LEAD.FUNNEL') }}
    </label>
    <select
      :value="data.funnelId || ''"
      class="w-full rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2 text-sm text-n-slate-12"
      @change="onFunnelChange"
    >
      <option value="">
        {{ t('AI_AGENTS.FLOW.LEAD.FUNNEL_NONE') }}
      </option>
      <option v-for="f in funnels" :key="f.id" :value="f.id">
        {{ f.name }}
      </option>
    </select>

    <!-- Etapa (coluna do kanban) -->
    <template v-if="selectedFunnel">
      <label class="mb-1 mt-3 block text-xs font-medium text-n-slate-11">
        {{ t('AI_AGENTS.FLOW.LEAD.STAGE') }}
      </label>
      <select
        :value="data.stageName || ''"
        class="w-full rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2 text-sm text-n-slate-12"
        @change="emit('update', { stageName: $event.target.value || null })"
      >
        <option value="">
          {{ t('AI_AGENTS.FLOW.LEAD.STAGE_FIRST') }}
        </option>
        <option v-for="s in stages" :key="s.name" :value="s.name">
          {{ s.label }}
        </option>
      </select>
    </template>

    <p
      v-if="!loading && !funnels.length"
      class="mt-2 flex items-start gap-2 rounded-lg border border-n-amber-6 bg-n-amber-2 px-3 py-2 text-xs text-n-amber-11"
    >
      <span class="i-lucide-alert-triangle mt-0.5 shrink-0" />
      <span>{{ t('AI_AGENTS.FLOW.LEAD.NONE') }}</span>
    </p>
    <p v-else class="mt-2 text-xs text-n-slate-10">
      {{ t('AI_AGENTS.FLOW.LEAD.HINT') }}
    </p>

    <button
      class="mt-4 w-full rounded-lg border border-n-ruby-7 py-2 text-sm font-medium text-n-ruby-9 hover:bg-n-ruby-3"
      @click="emit('remove')"
    >
      {{ t('AI_AGENTS.FLOW.REMOVE_NODE') }}
    </button>
  </div>
</template>
